import { useState, useEffect } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    FormControlLabel,
    Checkbox,
    Stack,
    Divider,
    Button,
} from "@mui/material";
import { ImagePaths } from "../../../Utility/ImagePaths";
import { TXN_MSG_MAX, TXN_MSG_MAX_LEN } from '../../../Utility/Constants';
import CommonValidation from "../../../Components/CommonFunctions/CommonValidation";
import { CurrencySymbol } from "../../../Utility/Constants";
import { CommonPatientDetails } from "../../../Components/Common Components/CommonComponents";

const CollectAdvanceModal = ({ open, onClose, onCollect, patientData, errorMessage, onSkip }) => {
    const [paymentMode, setPaymentMode] = useState({
        cash: true,
        card: false,
        upi: false,
        bank: false,
    });

    const [amounts, setAmounts] = useState({
        cashAmount: 0,
        upiAmount: 0,
        cardAmount: 0,
        bankAmount: 0,
        advance: 0,
        balance: 0,
        cardNumber: "",
        upiNumber: "",
        bankNumber: "",
    });

    // Autofill cash when only cash selected
    useEffect(() => {
        const selectedModes = Object.entries(paymentMode)
            .filter(([_, val]) => val)
            .map(([key]) => key);

        if (selectedModes.length === 1 && selectedModes[0] === "cash") {
            setAmounts((prev) => ({
                ...prev,
                cashAmount: prev.advance,
            }));
        } else {
            setAmounts((prev) => ({
                ...prev,
                cashAmount: "",
            }));
        }
    }, [amounts.advance, paymentMode]);

    const handleModeChange = (key) => {
        setPaymentMode((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (value === "" || CommonValidation.DecimalNumber(value)) {
            if (name === "advance") {
                var bal = ((patientData?.days * 2000) / 2) - value
                setAmounts((prev) => ({ ...prev, balance: bal }))
            }
            setAmounts((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleCollect = () => {
        const advanceSameAsPay = (+amounts?.cashAmount || 0) + (+amounts?.cardAmount || 0) + (+amounts?.upiAmount || 0) + (+amounts?.bankAmount || 0);
        if (+amounts.advance !== advanceSameAsPay) {
            errorMessage("Advance amount should match the collected amount")
        } else if (
            (amounts.cardAmount && (!amounts.cardNumber || amounts.cardNumber?.length < TXN_MSG_MAX_LEN)) ||
            (amounts.bankAmount && (!amounts.bankNumber || amounts.bankNumber?.length < TXN_MSG_MAX_LEN)) ||
            (amounts.upiAmount && (!amounts.upiNumber || amounts.upiNumber?.length < TXN_MSG_MAX_LEN))
        ) {
            errorMessage(TXN_MSG_MAX)
        } else if (onCollect) {
            onCollect({ ...amounts, ...paymentMode });
        }
    };

    const resetBtn = () => {
        setPaymentMode({
            cash: true,
            card: false,
            upi: false,
            bank: false,
        });
        setAmounts({
            cashAmount: "",
            upiAmount: "",
            cardAmount: "",
            bankAmount: "",
            advance: "",
            cardNumber: "",
            upiNumber: "",
            bankNumber: "",
        });
    }
    const onAdvanceClose = () => {
        resetBtn();
        onClose();
    }

    const renderAmountField = (key, label, icon, refKey) => (
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1, mb: 1 }}>
            <TextField
                label={`${label} (${CurrencySymbol})`}
                name={key}
                value={amounts[key]}
                onChange={handleChange}
                size="small"
                sx={{ width: "12dvw" }}
                InputProps={{
                    startAdornment: (
                        <img
                            src={icon}
                            alt={label}
                            className="eMed_action_img_small"
                            style={{ marginRight: 6 }}
                        />
                    ),
                }}
            />
            <TextField
                label="Reference Number *"
                name={refKey}
                value={amounts[refKey]}
                onChange={handleChange}
                size="small"
                sx={{ width: "15dvw" }}
            />
        </Stack>
    );

    return (
        <Dialog open={open} onClose={onAdvanceClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontWeight: 600, pb: 1 }}> Collect Advance </DialogTitle>
            <DialogContent sx={{ height: "60vh", paddingBottom: "0" }}>
                {/* Patient Details */}
                <Box display="flex" alignItems="center" mt={1} mb={2}>
                    <CommonPatientDetails data={patientData} />
                </Box>
                <Box display={"flex"} direction={"row"} gap={3} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Therapy Days"
                        name="Days"
                        value={patientData?.days}
                        variant="outlined"
                        size="small"
                        disabled
                    />
                    <TextField
                        fullWidth
                        label="Estimated Amount"
                        name="estimate"
                        value={(patientData?.days * 2000) / 2}
                        onChange={handleChange}
                        variant="outlined"
                        size="small"
                        disabled
                    />
                </Box>
                <Box display={"flex"} direction={"row"} gap={3} sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Advance Amount"
                        name="advance"
                        value={amounts?.advance}
                        onChange={handleChange}
                        variant="outlined"
                        size="small"
                    />
                    <TextField
                        fullWidth
                        label="Balance Amount"
                        name="balance"
                        value={amounts?.balance}
                        onChange={handleChange}
                        variant="outlined"
                        size="small"
                        disabled    
                    />
                </Box>

                {/* Payment Mode */}
                <Typography fontWeight={600} mb={1}>
                    Payment Mode
                </Typography>
                <Stack direction="row" spacing={2} mb={1}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={paymentMode.cash}
                                onChange={() => handleModeChange("cash")}
                            />
                        }
                        label="Cash"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={paymentMode.card}
                                onChange={() => handleModeChange("card")}
                            />
                        }
                        label="Card"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={paymentMode.upi}
                                onChange={() => handleModeChange("upi")}
                            />
                        }
                        label="UPI"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={paymentMode.bank}
                                onChange={() => handleModeChange("bank")}
                            />
                        }
                        label="Bank Transfer"
                    />
                </Stack>

                <Divider sx={{ mb: 1 }} />

                {/* CASH */}
                <Box overflow={"auto"} height={"20dvh"}>
                    {paymentMode.cash && (
                        <Stack direction="row" spacing={2} mt={1} mb={1}>
                            <TextField
                                label={`Cash Amount (${CurrencySymbol})`}
                                name="cashAmount"
                                value={amounts.cashAmount}
                                onChange={handleChange}
                                size="small"
                                sx={{ width: "12dvw" }}
                            />
                        </Stack>
                    )}

                    {/* CARD */}
                    {paymentMode.card &&
                        renderAmountField(
                            "cardAmount",
                            "Card Amount",
                            ImagePaths.CardIcon.default,
                            "cardNumber"
                        )}

                    {/* UPI */}
                    {paymentMode.upi &&
                        renderAmountField(
                            "upiAmount",
                            "UPI Amount",
                            ImagePaths.UPIIcon.default,
                            "upiNumber"
                        )}

                    {/* BANK */}
                    {paymentMode.bank &&
                        renderAmountField(
                            "bankAmount",
                            "Bank Amount",
                            ImagePaths.BankTransferIcon.default,
                            "bankNumber"
                        )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button variant="outlined" onClick={onAdvanceClose}>
                    Cancel
                </Button>
                <Button variant="outlined" color="secondary" onClick={() => {
                    resetBtn();
                    setTimeout(() => onSkip(), 0);
                }}>
                    Skip
                </Button>
                <Button variant="contained" color="primary" onClick={handleCollect}>
                    Collect
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CollectAdvanceModal;